'use client'

import { cn } from '@brew/ui/cn'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@brew/ui/tooltip'
import { useTranslations } from 'next-intl'
import { MicIcon } from '@/components/chat/icons'
import { Spinner } from '@/components/ui/spinner'

import { usePromptInputDictation } from './dictation-context'
import { PromptInputButton } from './primitives'

function prefetchDictation(): void {
  import('@/lib/dictation/stream-dictation').catch(() => undefined)
}

/**
 * Microphone toggle beside the composer's add-attachment button. Speech
 * streams into the prompt as it is spoken; press again, or send, to stop.
 */
export function PromptInputDictationButton() {
  const t = useTranslations('chat')
  const dictation = usePromptInputDictation()
  if (!dictation) {
    return null
  }
  const { status, isAvailable } = dictation
  const isActive = status !== 'idle'
  const isBusy = status === 'starting' || status === 'finishing'

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <PromptInputButton
            // One fixed name; `aria-pressed` carries on/off.
            aria-label={t('dictation.dictate')}
            aria-pressed={isActive}
            className={cn('relative', {
              'text-destructive': status === 'listening',
            })}
            disabled={!(isAvailable || isActive)}
            onClick={isActive ? dictation.stop : dictation.start}
            onFocus={prefetchDictation}
            onPointerEnter={prefetchDictation}
            data-state={status}
            data-testid="prompt-dictation-button"
            // Two child slots would otherwise pick the text-button size.
            size="icon-sm"
          >
            {isBusy ? <Spinner className="size-4" /> : <MicIcon size={16} />}
            {status === 'listening' ? (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 animate-pulse rounded-[inherit] ring-2 ring-destructive/40"
              />
            ) : null}
          </PromptInputButton>
        </TooltipTrigger>
        <TooltipContent side="top">
          {t(isActive ? 'dictation.stop' : 'dictation.dictate')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
